import React, { Component, Suspense } from 'react';
import { Spin } from 'antd';
import { observer, inject } from 'mobx-react';
import loadable from '@loadable/component';
import { BrowserRouter, Redirect, Route, Switch } from '../router-compat';

import Empty from '../components/empty';
import ConditionalRoute from '../components/conditional-route';
import Loader from '../components/loader';
import withTracker from '../tracker';
import handleUnauthorized from './hooks/handle-unauthorized';
import { retryLoadingChunk } from '../utils';

const loadableRetry = fn => {
  return loadable(() => retryLoadingChunk(fn), { fallback: <Loader /> });
};

const NotFound = loadableRetry(() => import(/* webpackChunkName: "NotFound" */ '../components/not-found'));
const Login = loadableRetry(() => import(/* webpackChunkName: "Login" */ '../auth/login'));
const Signup = loadableRetry(() => import(/* webpackChunkName: "Signup" */'../auth/signup'));
const PasswordReset = loadableRetry(() => import(/* webpackChunkName: "PasswordReset" */'../auth/password-reset'));
const NewPassword = loadableRetry(() => import(/* webpackChunkName: "NewPassword" */'../auth/new-password'));
const Onboarding = loadableRetry(() => import(/* webpackChunkName: "Onboarding" */'../onboarding'));
const Account = loadableRetry(() => import(/* webpackChunkName: "Account" */'../account'));
const Customer = loadableRetry(() => import(/* webpackChunkName: "Customer" */'../customer'));
const Dashboard = loadableRetry(() => import(/* webpackChunkName: "Dashboard" */'../dashboard'));
const Payout = loadableRetry(() => import(/* webpackChunkName: "Payout" */'../payout'));
const Verify = loadableRetry(() => import(/* webpackChunkName: "Verify" */'../verify'));
const Charge = loadableRetry(() => import(/* webpackChunkName: "Charge" */'../charge'));
const Admin = loadableRetry(() => import(/* webpackChunkName: "Admin" */'../admin'));
const EmailVerification = loadableRetry(() => import(/* webpackChunkName: "EmailVerification" */'../email-verification'));

@inject('app', 'uiStore', 'client')
@observer
class OrganizationRoutes extends Component {
  componentDidUpdate () {
    this.updateOrganization();
  }

  componentDidMount () {
    const { app, client, history } = this.props;
    this.updateOrganization();

    client.hooks({
      error: [handleUnauthorized(app, history)]
    });
  }

  updateOrganization () {
    const { app, match: { params: { organizationId } } } = this.props;

    // No app.organization happens after visiting an unknown organization id;
    // still try to switch so navigating to a valid organization recovers
    if (!app.organization || organizationId !== app.organization.id) {
      app.setOrganization(organizationId);
    }
  }

  render () {
    return (
      <Empty>
        <ConditionalRoute nav exact path="/organization/:organizationId/:section" component={Dashboard} />
      </Empty>
    );
  }
}

@inject('app')
@observer
class RootRoute extends Component {
  render () {
    const { app } = this.props;
    // Fall back to the first organization so the root route can recover when
    // app.organization is undefined (e.g. after visiting an unknown org id)
    const organizationId = app.organization?.id ?? app.user.organizations[0]?.id;

    return organizationId
      ? <Redirect to={`/organization/${organizationId}/charges`} />
      : <Spin size="large" />;
  }
}

export { OrganizationRoutes, RootRoute };

@inject('app')
@observer
class Routes extends Component {
  render () {
    const { app } = this.props;
    const isVerified = app.isAuthenticated && app.user.verified;
    const hasOrganization = isVerified && !!app.user.organizationIds.length;

    return (
      <Suspense fallback="Loading…">
        <BrowserRouter>
          <Switch>
            <ConditionalRoute exact path="/"
              component={withTracker(RootRoute)}
              condition={hasOrganization}
              redirect={app.isAuthenticated ? '/onboarding' : '/login'}
            />
            <ConditionalRoute path="/organization/:organizationId"
              component={withTracker(OrganizationRoutes)}
              condition={hasOrganization}
              redirect={app.isAuthenticated ? '/onboarding' : '/login'}
            />
            <ConditionalRoute exact nav path="/charge/:chargeId"
              component={withTracker(Charge)}
              condition={hasOrganization}
              redirect={app.isAuthenticated ? '/onboarding' : '/login'}
            />
            <ConditionalRoute exact nav path="/payout/:payoutId"
              component={withTracker(Payout)}
              condition={hasOrganization}
              redirect={app.isAuthenticated ? '/onboarding' : '/login'}
            />
            <ConditionalRoute exact nav path="/customer/:customerId"
              component={withTracker(Customer)}
              condition={hasOrganization}
              redirect={app.isAuthenticated ? '/onboarding' : '/login'}
            />
            <ConditionalRoute exact nav path="/account"
              component={withTracker(Account)}
              condition={app.isAuthenticated}
              redirect="/email-verification"
            />
            <ConditionalRoute exact nav path="/verify/:verificationId"
              component={withTracker(Verify)}
              condition={app.isAuthenticated}
              redirect="/login"
            />
            <ConditionalRoute exact path="/login"
              component={withTracker(Login)}
              condition={!app.isAuthenticated}
              redirect="/"
            />
            <ConditionalRoute exact path="/signup"
              component={withTracker(Signup)}
              condition={!app.isAuthenticated}
              redirect="/"
            />
            <ConditionalRoute exact path="/password-reset"
              component={withTracker(PasswordReset)}
              condition={!app.isAuthenticated}
              redirect="/"
            />
            <ConditionalRoute exact path="/onboarding"
              component={withTracker(Onboarding)}
              condition={isVerified}
              redirect="/email-verification"
            />
            <ConditionalRoute exact nav path="/email-verification"
              component={withTracker(EmailVerification)}
              condition={!isVerified && app.isAuthenticated}
              redirect="/login"
            />
            <ConditionalRoute exact nav path="/admin/:section"
              component={withTracker(Admin)}
              condition={app.isReseller}
              redirect="/"
            />
            <Route exact path="/new-password" component={withTracker(NewPassword)} />
            <Route component={withTracker(NotFound)} />
          </Switch>
        </BrowserRouter>
      </Suspense>
    );
  }
}

export default Routes;
